import { useCallback } from "react";

import isNil from "lodash/isNil";

import { TextTestID } from "@/constants/TestID";
import Icon, { FAIconName } from "@/ui/core/Icon";
import type { TypographyVariant } from "@/ui/core/Text";
import Text from "@/ui/core/Text";
import { Box, HStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

type TextTagVariant =
  | "disabled"
  | "highlight"
  | "informational"
  | "newFood"
  | "personInfo"
  | "selection"
  | "success";

interface TextTagStyle {
  backgroundColor: ColorToken;
  borderColor?: ColorToken;
  borderWidth?: number;
  paddingHorizontal: number;
  paddingVertical: number;
  textColor: ColorToken;
  textVariant: TypographyVariant;
}

const TextTagStyles: Record<TextTagVariant, TextTagStyle> = {
  disabled: {
    backgroundColor: ColorToken.NeutralLine,
    paddingHorizontal: 8,
    paddingVertical: 4,
    textColor: ColorToken.Secondary70,
    textVariant: "captionMedium",
  },
  highlight: {
    backgroundColor: ColorToken.TertiaryMarigoldBackground,
    paddingHorizontal: 8,
    paddingVertical: 4,
    textColor: ColorToken.TertiaryMarigoldText,
    textVariant: "captionMedium",
  },
  informational: {
    backgroundColor: ColorToken.Secondary10,
    paddingHorizontal: 8,
    paddingVertical: 4,
    textColor: ColorToken.Secondary70,
    textVariant: "captionMedium",
  },
  newFood: {
    backgroundColor: ColorToken.TertiaryPlumBackground,
    paddingHorizontal: 8,
    paddingVertical: 4,
    textColor: ColorToken.TertiaryPlumText,
    textVariant: "captionMedium",
  },
  personInfo: {
    backgroundColor: ColorToken.Primary10,
    paddingHorizontal: 6,
    paddingVertical: 1,
    textColor: ColorToken.Primary90Variant,
    textVariant: "overlineMedium",
  },
  selection: {
    backgroundColor: ColorToken.NeutralBackgroundPrimary,
    borderColor: ColorToken.NeutralLine,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    textColor: ColorToken.NeutralPrimary,
    textVariant: "captionMedium",
  },
  success: {
    backgroundColor: ColorToken.NotificationSuccessBackground,
    paddingHorizontal: 10,
    paddingVertical: 4,
    textColor: ColorToken.NotificationSuccessText,
    textVariant: "bodyMedium",
  },
};

const getVariantStyle = (variant?: TextTagVariant): TextTagStyle => {
  if (isNil(variant)) {
    return TextTagStyles.success;
  }

  return TextTagStyles[variant];
};

interface TextTagProps {
  onDismissPress?: () => void;
  testID?: string;
  text: string;
  variant?: TextTagVariant;
}

const TextTag = ({ onDismissPress, testID, text, variant }: TextTagProps) => {
  const {
    backgroundColor,
    borderColor,
    borderWidth,
    paddingHorizontal,
    paddingVertical,
    textColor,
    textVariant,
  } = getVariantStyle(variant);

  const onPress = useCallback(
    (event?: { stopPropagation?: () => void }) => {
      // on web, this prevents the parent component from also receiving this press
      // on native, this works as intended (only this box receives the press)
      if (isWeb()) event?.stopPropagation?.();
      onDismissPress?.();
    },
    [onDismissPress],
  );

  return (
    <HStack
      alignSelf="flex-start"
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderRadius={6}
      borderWidth={borderWidth}
      gap={8}
      paddingHorizontal={paddingHorizontal}
      paddingVertical={paddingVertical}
    >
      <Text
        accessibilityLabel={text}
        color={textColor}
        testID={testID ?? joinTestIDs(TextTestID.Tag, TextTestID.Text, text)}
        variant={textVariant}
      >
        {text}
      </Text>
      {variant === "selection" && !isNil(onDismissPress) && (
        <Box onPress={onPress}>
          <Icon
            color={ColorToken.NeutralPrimary}
            name={FAIconName.Times}
            size="sm"
            testID={joinTestIDs(TextTestID.Tag, "icon", text)}
          />
        </Box>
      )}
    </HStack>
  );
};

export type { TextTagProps };
export default TextTag;
export { TextTagStyles };
